import { FC } from 'react';
import { Button } from '@components/common/Button';
import { FormControl } from '@components/common/FormControl/FormControl';
import { FormCurrencyField } from '@components/common/FormCurrencyField/FormCurrencyField';
import { FormTextField } from '@components/common/FormTextField/FormTextField';
import { FormProvider, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { ICard } from '../CardsWrapper/types';
import { addCardQuestions } from './questions';
import { CardPayingOffOptionsEnum, IAddCardProps, ICardForm } from './types';
import { v4 as uuidv4 } from 'uuid';

export const AddCard: FC<IAddCardProps> = ({ cardToEdit, onGoBack, onSubmitCard }) => {
  const intl = useIntl();
  const requiredFieldErrorMessage = intl.formatMessage({ id: 'common.error.requiredField' });

  const form = useForm<ICardForm>({
    defaultValues: {
      balance: cardToEdit?.balance || '',
      firstName: cardToEdit?.firstName || '',
      lastName: cardToEdit?.lastName || '',
      payingOff: cardToEdit?.payingOff || '',
    },
  });

  const isCardHaveBalance = form.watch('payingOff') === CardPayingOffOptionsEnum.LEAVE_BALANCE;

  const submitFormHandler = (cardForm: ICardForm) => {
    const card: ICard = {
      id: cardToEdit?.id || uuidv4(),
      balance: isCardHaveBalance ? cardForm.balance || 0 : 0,
      payingOff: cardForm.payingOff,
      firstName: cardForm.firstName,
      lastName: cardForm.lastName,
    };

    onSubmitCard(card);
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(submitFormHandler)}>
        <div>
          <FormattedMessage id={addCardQuestions.fullName.labelKey} tagName={'h4'} />
          <FormTextField
            textFieldProps={{
              placeholder: intl.formatMessage({ id: addCardQuestions.fullName.firstNamePlaceholder }),
              type: 'text',
              className: 'mb-4',
            }}
            name={'firstName'}
            rules={{ required: requiredFieldErrorMessage }}
          />
          <FormTextField
            textFieldProps={{
              placeholder: intl.formatMessage({ id: addCardQuestions.fullName.lastNamePlaceholder }),
              type: 'text',
            }}
            name={'lastName'}
            rules={{ required: requiredFieldErrorMessage }}
          />
        </div>

        <div>
          <FormattedMessage id={addCardQuestions.payingOff.labelKey} tagName={'h4'} />
          <FormControl
            rules={{ required: requiredFieldErrorMessage }}
            name={'payingOff'}
            radioOptions={addCardQuestions.payingOff.options}
            radioGroupProps={{ className: 'mt-2' }}
          />
        </div>

        {isCardHaveBalance && (
          <div>
            <FormattedMessage id={addCardQuestions.balance.labelKey} tagName={'h4'} />
            <FormCurrencyField
              textFieldProps={{
                placeholder: addCardQuestions.balance.placeholder,
              }}
              name={'balance'}
              rules={{ required: requiredFieldErrorMessage }}
            />
          </div>
        )}

        <div className={'mt-8 flex justify-between'}>
          <Button variant={'outlined'} onClick={onGoBack}>
            <FormattedMessage id={'cta.back'} />
          </Button>

          <Button variant={'contained'} type={'submit'} disabled={!form.formState.isDirty}>
            <FormattedMessage id={'cta.continue'} />
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};
